import React, { useEffect, useMemo, useState, useRef } from "react";
import { PayGatePaymentPage } from '@aie/paygate';
import { PayGatePaymentSuccess, PayGateProvider } from '@aie/paygate';
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import '@aie/paygate/dist/paygate.css';
import { registerPayment, processProofOfPayment, progressOppStatus, sendPaymentReceivedEmail } from "../../../../services/ReRegistrationService";
import SkeletonTicketList from "../../../../loaders/SkeletonTicketList";
import { buildPaymentReceiptHtml, fetchPaymentByReference, generatePdfBlobFromHtml, resolvePdfApiUrl } from "@aie/paygate";
import { STUDENT_FILE_DOWNLOAD, zipURL } from "../../../../utils/Constants";
import { marketingUrl } from "../../../../utils/Constants";
import Swal from "sweetalert2";

const apiBase = process.env.REACT_APP_API_BASE || undefined;


/**
 * @typedef {Object} MakePaymentContainerProps
 * @property {0|1} isReReg - Whether the payment is for a re-registration (`1`) or
 *   a new student registration (`0`).
 * @property {string|number} studentId - The student or re-registration record ID.
 * @property {string} qualification_higher_enrolled - The qualification the student is enrolled in.
 * @property {boolean} isReturn - Whether the user is returning from a payment gateway redirect.
 * @property {boolean} isSuccess - Whether the payment was completed successfully.
 * @property {string} firstName - The payer's first name.
 * @property {string} lastName - The payer's last name.
 * @property {string} email - The payer's email address.
 * @property {string} mobile - The payer's mobile number.
 * @property {string} reference - The payment reference string.
 * @property {0|1} isFullPayment - Whether the payment is a full (`1`) or partial (`0`) payment.
 * @property {number} amount - The payment amount in cents.
 * @property {number} minAmount - The minimum acceptable payment amount in cents.
 * @property {string} returnUrl - The URL PayGate redirects to after a payment attempt.
 * @property {string} cancelUrl - The URL PayGate redirects to if the payment is cancelled.
 * @property {string} successUrl - The href passed to {@link PayGatePaymentSuccess} on completion.
 * @property {boolean} isCancel - Whether the payment was cancelled by the user.
 * @property {Function} amountCallback - Callback invoked with the paid amount (in cents)
 *   after the post-payment flow completes. Used to update parent state.
 */

/**
 * Container component that handles the full payment lifecycle for student registration
 * and re-registration.
 *
 * Responsibilities:
 * - Detects a returning PayGate redirect via the `m_payment_id` query parameter.
 * - On successful return, orchestrates the post-payment flow:
 *   1. Fetches payment data for the current payment reference.
 *   2. Generates an HTML receipt and converts it to a PDF blob.
 *   3. Submits the PDF as proof of payment, attaching it to the NetSuite opportunity.
 *   4. Registers the payment against the student record.
 *   5. Invokes `amountCallback` with the paid amount to update parent state.
 * - Renders a skeleton loader until `amount` and `reference` are available.
 * - Renders {@link PayGatePaymentSuccess} when `isReturn` or `isSuccess` is true.
 * - Renders {@link PayGatePaymentPage} with pre-filled payer details for new payments.
 *
 */
function MakePaymentContainer({
  isReReg,
  studentId,
  isReturn,
  isSuccess,
  firstName,
  lastName,
  email,
  mobile,
  reference,
  isFullPayment,
  amount,
  minAmount,
  returnUrl,
  cancelUrl,
  successUrl,
  isCancel,
  amountCallback
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const brandData = useSelector(
    state => state.brand.configuration
  );
  const headerUrl = `${STUDENT_FILE_DOWNLOAD}/Library/brand/${brandData?.letterheadHeader ? brandData?.letterheadHeader : brandData[0]?.letterheadHeader ? brandData[0]?.letterheadHeader : null
    }`;
  const footerUrl = `${STUDENT_FILE_DOWNLOAD}/Library/brand/${brandData?.letterheadFooter ? brandData?.letterheadFooter : brandData[0]?.letterheadFooter ? brandData[0]?.letterheadFooter : null
    }`;
  const location = useLocation();
  const successRef = useRef(null);

  const paymentId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("m_payment_id");
  }, [location.search]);

  // if we have a paymentId we know its success, we need to register it
  useEffect(() => {
    if (!paymentId) return;


    /**
     * 1. Fetch payment data for the current payment reference
     * 2. Generate a PDF receipt,
     * 3. Attach receipt to Netsuite Opportunity
     * 3.2 Progress Netsuite Stage and add FP discount line item - Applicable to Re-Reg only
     * 4. Register the payment for the student re-registration record.
     * 5. Update registration amount paid to remove warning badge
     * 6. Send Email
     * @returns {Promise<void>} Resolves when payment registration flow completes.
     */
    const registerPaymentData = async () => {
      setProcessingPayment(true);
      Swal.fire({
        icon: "info",
        title: "Processing Payment Information",
        text: "Please wait while we process your payment. This may take a few moments...",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      let filePath = "";
      let amount;
      let pdfGenerated = false;
      let POPAttached = false;
      let paymentRegistered = false;
      let updatedOpportunityStage = false;
      let pdf = null;
      // 1-3 Generate PDF and attach to NS opp
      try {
        // 1. Get payment data
        const paymentData = await fetchPaymentByReference(apiBase, paymentId);
        amount = paymentData?.amountCents || 1;
        // console.log("payment data", paymentData);
        // Create HTML for receipt
        const htmlContent = buildPaymentReceiptHtml({
          payment: paymentData
        });
        try {
          // 2 Generate pdf proof of payment
          pdf = await generatePdfBlobFromHtml({
            pdfApiUrl: resolvePdfApiUrl(apiBase, process.env.REACT_APP_PDF_API_URL),
            htmlContent,
            header: headerUrl,
            footer: footerUrl
          });
          if (pdf) {
            pdfGenerated = true;
          }
        } catch (e) {
          console.log("error generating pdf", e);
          pdfGenerated = e;
        }        
        // Create formdata to submit to processProofOfPayment endpoint
        const formData = new FormData();
        formData.append('doc', pdf);
        formData.append('studentId', studentId || null);
        formData.append('paymentId', paymentId);
        formData.append("amount", amount);
        formData.append("isFullPayment", isFullPayment === 1 ? true : false);
        formData.append("isReReg", isReReg === 1 ? true : false);
        // 3 Submit to processProofOfPayment endpoint
        const POPResponse = await processProofOfPayment(formData);
        if(POPResponse?.data?.url){
          POPAttached = true;
        }
        filePath = POPResponse?.data?.url;
      } catch (e) {
        if (e?.response?.status == 400) {
          console.log("proof of payment already uploaded");
          POPAttached = 'skipped';
        }else{
          POPAttached = e;
          console.log("error processing payment", e);
        }
      }
      // 3.2 Progress Netsuite Stage and add FP discount line item
      try {
        if (isReReg === 1) {
          await progressOppStatus({ studentId: studentId, isFullPayment: isFullPayment === 1 ? true : false });
          updatedOpportunityStage = true;
        }else{
          updatedOpportunityStage = 'skipped';
        }
      } catch (e) {
        updatedOpportunityStage = e;
        console.error("Error progressing opp status", e);
      }
      try {
        // 4. Register payment
        await registerPayment({ isReReg: isReReg === 1 ? true : false, studentId: studentId, paymentRef: paymentId, filePath: filePath || null });
        paymentRegistered = true;
      } catch (e) {
        if (e?.response?.status == 400) {
          //payment already registered do nothing
          paymentRegistered = 'skipped';
          console.log("payment already registered");
        } else {
          paymentRegistered = e;
          console.error(e);
        }
      }
      // 5. Update registration amount paid to remove warning badge
      try {
        if (amountCallback) {
          amountCallback(Number(amount) || 1);
        }
      } catch (e) {
        console.error(e);
        // reload page
        window.location.reload();
      }
            // 6. Send Email
      try {
        const statusObj = {
          pdfGenerated: pdfGenerated,
          POPAttached: POPAttached,
          paymentRegistered: paymentRegistered,
          updatedOpportunityStage: updatedOpportunityStage 
        }
        await sendPaymentReceivedEmail({ amount: amount, filePath: filePath, studentId: studentId, isReReg: isReReg === 1 ? true : false, paymentRef: paymentId, status: statusObj });
      } catch (error) {
        console.error(error);
      }
        Swal.close();
        setProcessingPayment(false);
    };
    registerPaymentData();
  }, [paymentId]);


  useEffect(() => {
    if (!amount || !reference) return
    setIsLoading(false);
  }, [amount, reference]);

  return (
    <>
      {isLoading || processingPayment ? <SkeletonTicketList /> :
        <div className="payment-container">
          {(isReturn || isSuccess) ?
            (
              <PayGateProvider
                config={{
                  apiBase,
                  returnUrl: returnUrl,
                  cancelUrl: cancelUrl,
                  ...(process.env.REACT_APP_PDF_API_URL
                    ? { pdfApiUrl: process.env.REACT_APP_PDF_API_URL }
                    : {}),
                }}
              >
                <PayGatePaymentSuccess
                  returnHref={successUrl}
                  ref={successRef}
                />
              </PayGateProvider>
            ) :
            (
              <PayGatePaymentPage
                apiBase={apiBase}
                returnUrl={returnUrl}
                cancelUrl={cancelUrl}
                minAmount={minAmount}
                alternatePaymentLink={
                  `${marketingUrl}/payment?firstname=${encodeURIComponent(firstName)}&lastname=${encodeURIComponent(lastName)}&email=${encodeURIComponent(email)}&mobile=${encodeURIComponent(mobile)}&amount=${amount}&payerReference=${encodeURIComponent(reference)}&studentId=${studentId}&isReReg=${isReReg}&isFP=${isFullPayment}`
                }
                alternatePaymentLinkLabel={`${marketingUrl}/payment`}
                submitLabel={"Make Payment"}
                defaultValues={{
                  firstName: firstName,
                  lastName: lastName,
                  mobile: mobile,
                  email: email,
                  amount: amount,
                  payerReference: reference,
                }}
                paymentFailed={isCancel}
                disableReference={true}
              />
            )}
        </div>
      }
    </>
  );
}

export default React.memo(MakePaymentContainer);

